import React, { useState, useEffect } from 'react';
import { RiskMatrix, RiskTolerability } from '../types';
import { riskService } from '../riskService';

interface RiskMatrixHeatmapProps {
  matrix?: RiskMatrix | null;
  distribution?: Record<string, number>;
  selectedCell?: string;
  onCellClick?: (cellIndex: string) => void;
  useResidual?: boolean;
  onToggleResidual?: (val: boolean) => void;
}

export const RiskMatrixHeatmap: React.FC<RiskMatrixHeatmapProps> = ({
  matrix: propMatrix,
  distribution: propDistribution,
  selectedCell,
  onCellClick,
  useResidual: propUseResidual,
  onToggleResidual: propOnToggleResidual,
}) => {
  const [internalUseResidual, setInternalUseResidual] = useState(false);
  const [internalMatrix, setInternalMatrix] = useState<RiskMatrix | null>(null);
  const [internalDistribution, setInternalDistribution] = useState<Record<string, number>>({});

  const useResidual = propUseResidual !== undefined ? propUseResidual : internalUseResidual;
  const matrix = propMatrix !== undefined ? propMatrix : internalMatrix;
  const distribution = propDistribution !== undefined ? propDistribution : internalDistribution;

  const handleToggleResidual = (val: boolean) => {
    if (propOnToggleResidual) {
      propOnToggleResidual(val);
    } else {
      setInternalUseResidual(val);
    }
  };

  useEffect(() => {
    if (propMatrix === undefined || propDistribution === undefined) {
      riskService
        .getHeatmapData('ORG-XYZ', useResidual)
        .then((res) => {
          if (res?.matrix) setInternalMatrix(res.matrix);
          if (res?.distribution) setInternalDistribution(res.distribution);
        })
        .catch((err) => {
          console.error('Failed to load matrix heatmap data:', err);
        });
    }
  }, [propMatrix, propDistribution, useResidual]);

  const severities = [
    { num: 5, label: '5 - Catastrophic' },
    { num: 4, label: '4 - Hazardous' },
    { num: 3, label: '3 - Major' },
    { num: 2, label: '2 - Minor' },
    { num: 1, label: '1 - Negligible' },
  ];

  const likelihoods = [
    { code: 'A', label: 'A - Frequent' },
    { code: 'B', label: 'B - Probable' },
    { code: 'C', label: 'C - Remote / Possible' },
    { code: 'D', label: 'D - Extremely Improbable' },
    { code: 'E', label: 'E - Extremely Improbable (Rare)' },
  ];

  const getDefaultCellTolerability = (sev: number, lik: string): { tolerability: RiskTolerability; colorHex: string } => {
    const isIntolerable =
      (sev === 5 && ['A', 'B', 'C'].includes(lik)) ||
      (sev === 4 && ['A', 'B'].includes(lik)) ||
      (sev === 3 && lik === 'A');
    const isAcceptable =
      (sev === 3 && lik === 'E') ||
      (sev === 2 && ['D', 'E'].includes(lik)) ||
      sev === 1;
    if (isIntolerable) return { tolerability: 'INTOLERABLE', colorHex: '#ef4444' };
    if (isAcceptable) return { tolerability: 'ACCEPTABLE', colorHex: '#10b981' };
    return { tolerability: 'TOLERABLE', colorHex: '#f59e0b' };
  };

  const getCellData = (sev: number, lik: string) => {
    const idx = `${sev}${lik}`;
    const found = matrix?.cells?.find((c) => c.severity === sev && c.likelihood === lik);
    const count = distribution?.[idx] || 0;
    const fallback = getDefaultCellTolerability(sev, lik);

    const tolerability: RiskTolerability = found?.tolerability || fallback.tolerability;
    const bg = found?.colorHex || fallback.colorHex;

    return {
      index: idx,
      count,
      tolerability,
      bg,
    };
  };

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
      }}
    >
      {/* Matrix Controls & Title */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: 700 }}>
            ICAO Doc 9859 Safety Risk Tolerability Matrix (5x5 Heatmap)
          </h3>
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            Interactive Risk Distribution & ALARP Operational Boundaries
          </span>
        </div>

        {/* Toggle Initial vs Residual */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Display:</span>
          <button
            onClick={() => handleToggleResidual(false)}
            style={{
              padding: '6px 14px',
              borderRadius: '6px 0 0 6px',
              border: '1px solid #cbd5e1',
              background: !useResidual ? '#0284c7' : '#ffffff',
              color: !useResidual ? '#ffffff' : '#475569',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Initial Risk
          </button>
          <button
            onClick={() => handleToggleResidual(true)}
            style={{
              padding: '6px 14px',
              borderRadius: '0 6px 6px 0',
              border: '1px solid #cbd5e1',
              borderLeft: 'none',
              background: useResidual ? '#0284c7' : '#ffffff',
              color: useResidual ? '#ffffff' : '#475569',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Residual Risk (Post-Controls)
          </button>
        </div>
      </div>

      {/* Grid Container */}
      <div style={{ display: 'grid', gridTemplateColumns: '160px repeat(5, 1fr)', gap: '6px' }}>
        {/* Top-left empty header */}
        <div style={{ padding: '8px', fontSize: '12px', fontWeight: 700, color: '#64748b', textAlign: 'center' }}>
          Severity ↓ / Likelihood →
        </div>

        {/* Columns Header (Likelihood) */}
        {likelihoods.map((l) => (
          <div
            key={l.code}
            style={{
              padding: '8px 4px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              textAlign: 'center',
              fontSize: '11px',
              fontWeight: 700,
              color: '#334155',
            }}
          >
            {l.label}
          </div>
        ))}

        {/* Rows (Severity) & Cells */}
        {severities.map((s) => (
          <React.Fragment key={s.num}>
            {/* Row Label */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 10px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 700,
                color: '#334155',
              }}
            >
              {s.label}
            </div>

            {/* Cells for this severity row */}
            {likelihoods.map((l) => {
              const cell = getCellData(s.num, l.code);
              const isSelected = selectedCell === cell.index;

              return (
                <div
                  key={cell.index}
                  onClick={() => onCellClick && onCellClick(cell.index)}
                  style={{
                    background: cell.bg,
                    color: '#ffffff',
                    height: '64px',
                    borderRadius: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: isSelected ? '0 0 0 3px #1e293b, 0 4px 6px rgba(0,0,0,0.1)' : 'none',
                    transform: isSelected ? 'scale(1.02)' : 'none',
                    transition: 'all 0.15s ease',
                    position: 'relative',
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: 800, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
                    {cell.index}
                  </span>

                  {cell.count > 0 ? (
                    <span
                      style={{
                        background: '#ffffff',
                        color: '#0f172a',
                        fontWeight: 800,
                        fontSize: '11px',
                        padding: '1px 8px',
                        borderRadius: '12px',
                        marginTop: '2px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      }}
                    >
                      {cell.count} {cell.count === 1 ? 'Risk' : 'Risks'}
                    </span>
                  ) : (
                    <span style={{ fontSize: '10px', opacity: 0.75, marginTop: '2px' }}>0</span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Tolerability Legend */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '20px',
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: '1px solid #f1f5f9',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '14px', height: '14px', background: '#ef4444', borderRadius: '3px' }} />
          <span>INTOLERABLE (Unacceptable / Stop Operations)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '14px', height: '14px', background: '#f59e0b', borderRadius: '3px' }} />
          <span>TOLERABLE (ALARP Mitigation Required)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '14px', height: '14px', background: '#10b981', borderRadius: '3px' }} />
          <span>ACCEPTABLE (Routine Monitoring)</span>
        </div>
      </div>
    </div>
  );
};
